// PerformanceBenchmark.js
// Tools for measuring and analyzing performance of island generation and rendering

/**
 * PerformanceBenchmark - Provides tools for measuring performance of island generation and rendering
 */
class PerformanceBenchmark {
  /**
   * Constructor
   */
  constructor() {
    this.measurements = {
      generationTime: {
        totalTime: 0,
        islandMask: 0,
        heightMap: 0,
        biomeMap: 0,
        terrainMesh: 0,
        texturing: 0,
        vegetation: 0,
        landmarks: 0,
        optimization: 0
      },
      renderPerformance: {
        fps: 0,
        frameTimeAvg: 0,
        frameTimeMin: Infinity,
        frameTimeMax: 0,
        frameTimeHistory: []
      }
    };
    
    this.isGenerationMeasuring = false;
    this.isRenderMeasuring = false;
    this.generationStartTime = 0;
    this.frameCounter = 0;
    this.lastFpsUpdate = 0;
    this.totalFrameTime = 0;
    
    // Store callbacks for sending performance data
    this.onPerformanceUpdate = null;
    
    // Make non-reactive if in a Vue context
    return typeof markRaw !== 'undefined' ? markRaw(this) : this;
  }
  
  /**
   * Start measuring generation time
   */
  startGenerationMeasurement() {
    // Reset generation measurements
    Object.keys(this.measurements.generationTime).forEach(key => {
      this.measurements.generationTime[key] = 0;
    });
    
    this.isGenerationMeasuring = true;
    this.generationStartTime = performance.now();
    
    console.log('Starting generation benchmark...');
  }
  
  /**
   * Measure a specific part of the generation process
   * @param {string} phase - The phase being measured (e.g., 'islandMask', 'terrainMesh')
   */
  measureGenerationPhase(phase) {
    if (!this.isGenerationMeasuring) return;
    
    const now = performance.now();
    const elapsedTime = now - this.generationStartTime;
    this.generationStartTime = now;
    
    if (this.measurements.generationTime.hasOwnProperty(phase)) {
      this.measurements.generationTime[phase] = elapsedTime;
      this.measurements.generationTime.totalTime += elapsedTime;
      console.log(`Generation phase "${phase}" took ${elapsedTime.toFixed(2)}ms`);
    }
  }
  
  /**
   * End generation measurement
   * @returns {Object} The generation time measurements
   */
  endGenerationMeasurement() {
    this.isGenerationMeasuring = false;
    
    // Calculate generation percentages
    const result = {
      ...this.measurements.generationTime,
      percentages: {}
    };
    
    const totalTime = this.measurements.generationTime.totalTime;
    
    if (totalTime > 0) {
      Object.keys(this.measurements.generationTime).forEach(key => {
        if (key !== 'totalTime') {
          result.percentages[key] = (this.measurements.generationTime[key] / totalTime * 100).toFixed(1) + '%';
        }
      });
    }
    
    console.log('Generation benchmark completed');
    console.log('Total generation time:', totalTime.toFixed(2), 'ms');
    console.log('Generation breakdown:', result.percentages);
    
    return result;
  }
  
  /**
   * Start measuring rendering performance
   * @param {number} sampleSize - Number of frames to measure (default: 300)
   */
  startRenderMeasurement(sampleSize = 300) {
    // Reset render measurements
    this.measurements.renderPerformance.fps = 0;
    this.measurements.renderPerformance.frameTimeAvg = 0;
    this.measurements.renderPerformance.frameTimeMin = Infinity;
    this.measurements.renderPerformance.frameTimeMax = 0;
    this.measurements.renderPerformance.frameTimeHistory = [];
    
    this.isRenderMeasuring = true;
    this.frameCounter = 0;
    this.lastFpsUpdate = performance.now();
    this.totalFrameTime = 0;
    this.sampleSize = sampleSize;
    
    console.log(`Starting render performance measurement (${sampleSize} frames)...`);
  }
  
  /**
   * Measure a frame
   * @param {number} deltaTime - Time since last frame in seconds
   * @returns {boolean} True if still measuring, false if measurement is complete
   */
  measureFrame(deltaTime) {
    if (!this.isRenderMeasuring) return false;
    
    // Calculate frame time in milliseconds
    const frameTime = deltaTime * 1000;
    
    // Update statistics
    this.frameCounter++;
    this.totalFrameTime += frameTime;
    this.measurements.renderPerformance.frameTimeMin = Math.min(this.measurements.renderPerformance.frameTimeMin, frameTime);
    this.measurements.renderPerformance.frameTimeMax = Math.max(this.measurements.renderPerformance.frameTimeMax, frameTime);
    
    // Store frame time in history (up to 300 frames)
    this.measurements.renderPerformance.frameTimeHistory.push(frameTime);
    if (this.measurements.renderPerformance.frameTimeHistory.length > 300) {
      this.measurements.renderPerformance.frameTimeHistory.shift();
    }
    
    // Update FPS every second
    const now = performance.now();
    if (now - this.lastFpsUpdate >= 1000) {
      const elapsedSeconds = (now - this.lastFpsUpdate) / 1000;
      this.measurements.renderPerformance.fps = this.frameCounter / elapsedSeconds;
      this.frameCounter = 0;
      this.lastFpsUpdate = now;
      
      // Call update callback if set
      if (this.onPerformanceUpdate) {
        this.onPerformanceUpdate({
          fps: this.measurements.renderPerformance.fps,
          frameTime: this.totalFrameTime / this.measurements.renderPerformance.frameTimeHistory.length
        });
      }
    }
    
    // Check if we've collected enough samples
    if (this.measurements.renderPerformance.frameTimeHistory.length >= this.sampleSize) {
      this.endRenderMeasurement();
      return false;
    }
    
    return true;
  }
  
  /**
   * End render measurement
   * @returns {Object} The render performance measurements
   */
  endRenderMeasurement() {
    if (!this.isRenderMeasuring) return this.measurements.renderPerformance;
    
    this.isRenderMeasuring = false;
    
    // Calculate average frame time
    const frameTimeAvg = this.totalFrameTime / this.measurements.renderPerformance.frameTimeHistory.length;
    this.measurements.renderPerformance.frameTimeAvg = frameTimeAvg;
    
    console.log('Render performance measurement completed');
    console.log('FPS:', this.measurements.renderPerformance.fps.toFixed(1));
    console.log('Average frame time:', frameTimeAvg.toFixed(2), 'ms');
    console.log('Min frame time:', this.measurements.renderPerformance.frameTimeMin.toFixed(2), 'ms');
    console.log('Max frame time:', this.measurements.renderPerformance.frameTimeMax.toFixed(2), 'ms');
    
    return this.measurements.renderPerformance;
  }
  
  /**
   * Get the current measurements
   * @returns {Object} The current measurements
   */
  getMeasurements() {
    return {
      generationTime: {...this.measurements.generationTime},
      renderPerformance: {...this.measurements.renderPerformance}
    };
  }
  
  /**
   * Set a callback function to be called when performance data is updated
   * @param {Function} callback - Function to call with performance data
   */
  setPerformanceUpdateCallback(callback) {
    this.onPerformanceUpdate = callback;
  }
  
  /**
   * Calculate statistics for the collected frame times
   * @returns {Object} Statistics object with percentiles
   */
  calculateFrameTimeStatistics() {
    const frameTimes = [...this.measurements.renderPerformance.frameTimeHistory];
    frameTimes.sort((a, b) => a - b);
    
    const length = frameTimes.length;
    if (length === 0) return {};
    
    // Calculate percentiles
    const p50 = frameTimes[Math.floor(length * 0.5)];
    const p90 = frameTimes[Math.floor(length * 0.9)];
    const p95 = frameTimes[Math.floor(length * 0.95)];
    const p99 = frameTimes[Math.floor(length * 0.99)];
    
    return {
      mean: this.measurements.renderPerformance.frameTimeAvg,
      min: this.measurements.renderPerformance.frameTimeMin,
      max: this.measurements.renderPerformance.frameTimeMax,
      median: p50,
      p90: p90,
      p95: p95,
      p99: p99,
      fps: this.measurements.renderPerformance.fps,
      sampleSize: length
    };
  }
}

export default PerformanceBenchmark; 